import type { ChatForm as ChatFormValues } from '../../../schema/spark/chat-form-schema';
import { ChatMessageOriginators } from '../../enums/chat-message-originators';

import { SparkSendTaskUtil } from './spark-send-task-util';
import type { SparkSession } from './spark-session.types';

/**
 * SparkSendUtil - I turn a submitted prompt into a task the queue will run. ONLY concerned with
 * what happens between the composer and the queue - the task itself does the sending.
 */
export const SparkSendUtil = {
  async submitPrompt({
    chat,
    session,
  }: {
    chat: ChatFormValues['chat'];
    session: SparkSession;
  }) {
    const { chatFormRef, queue, shelf, status, transcript } = session;

    if (transcript.isChatFull) return;

    /*
      **Text is required, attachment or not** - `text` is `@NotNull @NotEmpty`. Refused here
      rather than at the 400 because the upload has already run by then: a file-only send left
      orphaned bytes and, on a first prompt, a chat for a conversation that never happened.
    */
    const prompt = chat?.trim() ?? '';

    if (!prompt) return;

    status.setIsLoading(true);
    // Whatever it said is about the send that just got superseded.
    status.setNotice(null);

    /*
      The whole shelf, emptied in one go: every attached file goes with this prompt now, and the
      list is captured here rather than read at send time so a prompt waiting its turn keeps the
      files it was submitted with however the shelf changes behind it.
    */
    const staged = shelf.attachmentsRef.current;

    shelf.putAttachments([]);

    const entry = {
      // The local `File` names: the turn renders before the uploads have answered, so no ids yet.
      attachments:
        staged.length > 0
          ? staged.map(({ file }) => ({ fileName: file.name }))
          : undefined,
      id: crypto.randomUUID(),
      messageOriginator: ChatMessageOriginators.USER,
      text: prompt,
    } as const;

    const editing = queue.takeEditing();

    queue.stagePrompt({
      attachments: staged,
      entry,
      replacePromptId: editing?.promptId,
    });

    // Emptied as the prompt is sent, so the composer is ready for the next one while the answer
    // is still coming back.
    chatFormRef.current?.resetForm();

    const sent = queue.enqueuePrompt({
      // `undefined` appends; an edited prompt carries the index it was taken from.
      index: editing?.index,
      // Keyed by the prompt, so it can be taken back out of the queue before its turn.
      key: entry.id,
      task: async () =>
        SparkSendTaskUtil.runSend({
          attachments: staged,
          chat,
          entry,
          prompt,
          session,
        }),
    });

    /*
      Resumed between the enqueue and the await: after it deadlocks (the promise resolves only
      once the task runs), before it lets the queue take whatever sat at that index first.
    */
    if (editing) queue.resumeQueue();

    await sent;
  },
};
